import { useContext, useEffect, useState } from "react";
import { Calendar } from "primereact/calendar";
import { addLocale } from "primereact/api";
import styled from "styled-components";
import OrgTreeSelect from "../UI/OrgTreeSelect/OrgTreeSelect";
import IndividualDashboard from "./Overviews/IndividualDashboard";
import OversightDashboard from "./Overviews/OversightDashboard";

import { CurrentUserContext } from "../../Contexts/CurrentUserContext";
import { OrgTreeContext } from "../../Contexts/OrgTreeContext";
import { ReportDatabaseContext } from "../../Contexts/ReportDatabaseContext";

import { findNodeById } from "../../Helpers/OrgTreeFunctions";

const DashboardWrapper = styled.div`
  padding: 15px 0;
  width: 100%;
  h1 {
    margin: 10px;
    color: var(--accent-color-dark);
    font-size: 25px;
    font-weight: 500;
  }
  @media (max-width: 400px) {
    h1 {
      font-size: 20px;
    }
  }
`;

const Dasboards = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: space-around;
`;

const SelectWrapper = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;

  p.full-name {
  }

  .p-inputtext {
    width: 220px;
    text-align: center;
  }
  label {
    font-size: 12px;
    font-weight: 200;
    width: 100%;
    margin-bottom: 3px;
    text-align: center;
  }
  .select-box {
    display: flex;
    flex-direction: column;
  }
`;

function Dashboard() {
  const userContext = useContext(CurrentUserContext);
  const { setNodesNeeded, reportData } = useContext(ReportDatabaseContext);
  const currentUser = userContext?.currentUser;
  const orgTree = useContext(OrgTreeContext);
  const [dates, setDates] = useState([new Date(), new Date()]);
  const [selectedTeam, setSelectedTeam] = useState<OrgTreeItem>();

  useEffect(() => {
    const currentDate = new Date();
    while (currentDate.getDay() !== 0) {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    // Subtract 28 days to get a date that's 4 weeks ago
    const fourWeeksBack = new Date(currentDate);
    fourWeeksBack.setDate(fourWeeksBack.getDate() - 28);

    // Adjust to the previous Monday if it's not already a Thursday
    while (fourWeeksBack.getDay() !== 1) {
      fourWeeksBack.setDate(fourWeeksBack.getDate() + 1);
    }

    setDates([fourWeeksBack, currentDate]);
  }, []);

  const onlyIndividualOverView = !currentUser?.PCRoles.some(
    (role) =>
      role.role !== "volunteer" &&
      role.role !== "churchStaff" &&
      role.role !== "pastoralCare"
  );

  /* If no selected team (on first render) set the parent tree of the user's first role with a  */
  useEffect(() => {
    if (currentUser && !selectedTeam && orgTree) {
      //if the user has only a reporting role, set the selected team to themselfs
      if (!onlyIndividualOverView) {
        //if user has a no reporting role set selected team to the first role that has a teamId
        const currentUserOversightCampusId = currentUser.PCRoles.find(
          (role) =>
            role.role !== "volunteer" &&
            role.role !== "churchStaff" &&
            role.role !== "pastoralCare" &&
            role.role !== "admin"
        )?.teamId;
        if (!currentUserOversightCampusId) return;
        const currentUserOVersightCampus = findNodeById(
          [orgTree],
          currentUserOversightCampusId
        );

        currentUserOVersightCampus &&
          setSelectedTeam(currentUserOVersightCampus);
      } else {
        const currentUserNode: OrgTreeItem = {
          key: currentUser.userId,
          teamId: currentUser.userId,
          teamType: "volunteer",
          label: currentUser.firstName + " " + currentUser.lastName,
          visible: true,
        };
        setSelectedTeam(currentUserNode);
      }
    }
  }, [currentUser, orgTree, selectedTeam, onlyIndividualOverView]);

  //Add the selectedTree element to the orgTrees that have to be fetched from the report database
  useEffect(() => {
    if (!selectedTeam) return;
    setNodesNeeded((prevState) => {
      if (!prevState) return [selectedTeam];
      if (prevState.some((team) => team.teamId === selectedTeam.teamId))
        return prevState;
      return [...prevState, selectedTeam];
    });
  }, [selectedTeam]);

  const customLocale = {
    firstDayOfWeek: 1,
    dayNames: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    dayNamesMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    // ... add other locale settings if needed
  };

  addLocale("thursdayFirstLocale", customLocale);

  return (
    <DashboardWrapper>
      <h1>{onlyIndividualOverView && "My"} Pastoral Care Overview</h1>
      <SelectWrapper>
        <span className="select-box">
          {!onlyIndividualOverView && (
            <>
              <label htmlFor="title">Team Selection</label>
              <OrgTreeSelect
                includeUsers={true}
                selectedTeam={selectedTeam}
                teamSelect={setSelectedTeam}
              />{" "}
            </>
          )}
        </span>
        <span className="select-box">
          <label htmlFor="title">Period Selection</label>
          <Calendar
            value={dates}
            onChange={(e) => Array.isArray(e.value) && setDates(e.value)}
            selectionMode="range"
            minDate={new Date("01-01-2018")}
            maxDate={new Date()}
            dateFormat="dd/mm/yy"
            locale="thursdayFirstLocale"
          />
        </span>
      </SelectWrapper>

      <Dasboards>
        {(selectedTeam?.teamType === "volunteer" ||
          selectedTeam?.teamType === "churchStaff" ||
          selectedTeam?.teamType === "pastoralCare") && (
          <IndividualDashboard
            dates={dates}
            selectedNodeId={selectedTeam && selectedTeam?.teamId}
          />
        )}
        {selectedTeam &&
          selectedTeam?.teamType !== "volunteer" &&
          selectedTeam?.teamType !== "churchStaff" &&
          selectedTeam?.teamType !== "pastoralCare" && (
            <OversightDashboard dates={dates} selectedNode={selectedTeam} />
          )}
      </Dasboards>
    </DashboardWrapper>
  );
}

export default Dashboard;
